import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Endpoints } from 'app/core/constants/Endpoints';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { environment } from 'environments/environment';
import {
    Observable,
    ReplaySubject,
    catchError,
    defaultIfEmpty,
    map,
    of,
    take,
} from 'rxjs';

interface NotificationHistoryItem {
    id?: string | number;
    orderId?: string | number;
    title?: string;
    message?: string;
    notificationType?: string;
    read?: boolean;
    active?: boolean;
    createdAt?: string;
    metadata?: Record<string, string>;
}

interface NotificationHistoryResponse {
    status?: boolean;
    message?: string;
    notifications?: NotificationHistoryItem[];
    unreadCount?: number;
    markedCount?: number;
}

@Injectable({ providedIn: 'root' })
export class NotificationsService {
    private readonly _httpClient = inject(HttpClient);
    private readonly _baseURL = environment.apiBaseURL;
    private readonly _fetchLimit = 1000;
    private readonly _pushDedupWindowMs = 5000;

    private _notifications: ReplaySubject<Notification[]> = new ReplaySubject<
        Notification[]
    >(1);
    private _cache: Notification[] = [];
    private _recentPushTimes = new Map<string, number>();
    private _refreshTimer: ReturnType<typeof setTimeout> | null = null;

    constructor() {
        this._notifications.next([]);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for notifications
     */
    get notifications$(): Observable<Notification[]> {
        return this._notifications.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all notifications
     */
    getAll(): Observable<Notification[]> {
        return this._httpClient
            .get<NotificationHistoryResponse>(
                `${this._baseURL}${Endpoints.notifications.list}`,
                {
                    params: {
                        limit: String(this._fetchLimit),
                    },
                }
            )
            .pipe(
                map((response) => this._emit(this._mapResponse(response))),
                catchError((error) => {
                    console.warn(
                        'Unable to fetch notifications from API. Keeping cached notifications.',
                        error
                    );
                    this._notifications.next([...this._cache]);
                    return of([...this._cache]);
                }),
                defaultIfEmpty([...this._cache])
            );
    }

    /**
     * Create a notification
     *
     * @param notification
     */
    create(notification: Notification): Observable<Notification> {
        return this.notifications$.pipe(
            take(1),
            map((notifications) => {
                const newNotification: Notification = {
                    ...notification,
                    id: notification.id || this._buildClientNotificationId(),
                    read: Boolean(notification.read),
                    time: notification.time || new Date().toISOString(),
                };

                this._emit([newNotification, ...notifications]);
                return newNotification;
            })
        );
    }

    /**
     * Update the notification
     *
     * @param id
     * @param notification
     */
    update(id: string, notification: Notification): Observable<Notification> {
        const normalizedId = String(id);

        if (notification.read && this._isServerNotificationId(normalizedId)) {
            this._httpClient
                .post<NotificationHistoryResponse>(
                    `${this._baseURL}${Endpoints.notifications.markReadBase}/${encodeURIComponent(
                        normalizedId
                    )}/read`,
                    {}
                )
                .pipe(
                    take(1),
                    catchError((error) => {
                        console.warn(
                            `Failed to mark notification ${normalizedId} as read in API.`,
                            error
                        );
                        return of(null);
                    }),
                    defaultIfEmpty(null)
                )
                .subscribe();
        }

        return this._updateLocalNotification(normalizedId, notification);
    }

    /**
     * Delete the notification
     *
     * @param id
     */
    delete(id: string): Observable<boolean> {
        const normalizedId = String(id);

        if (!this._isServerNotificationId(normalizedId)) {
            return this.notifications$.pipe(
                take(1),
                map(() => {
                    this._removeLocal(normalizedId);
                    return true;
                })
            );
        }

        return this._httpClient
            .post<NotificationHistoryResponse>(
                `${this._baseURL}${Endpoints.notifications.deactivateBase}/${encodeURIComponent(
                    normalizedId
                )}/deactivate`,
                {}
            )
            .pipe(
                take(1),
                map((response) => {
                    if (response?.status === false) {
                        return false;
                    }

                    this._removeLocal(normalizedId);
                    return true;
                }),
                catchError((error) => {
                    console.warn(`Failed to deactivate notification ${normalizedId}.`, error);
                    return of(false);
                }),
                defaultIfEmpty(false)
            );
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): Observable<boolean> {
        this._httpClient
            .post<NotificationHistoryResponse>(
                `${this._baseURL}${Endpoints.notifications.markAllRead}`,
                {}
            )
            .pipe(
                take(1),
                catchError((error) => {
                    console.warn('Failed to mark all notifications as read in API.', error);
                    return of(null);
                }),
                defaultIfEmpty(null)
            )
            .subscribe();

        const updated = this._cache.map((notification) => ({
            ...notification,
            read: true,
        }));
        this._emit(updated);
        return of(true);
    }

    ingestPushPayload(payload: any): void {
        const messageKey = this._buildMessageKey(payload);
        if (!this._shouldProcessPush(messageKey)) {
            return;
        }

        const optimisticNotification = this._mapPushPayload(payload, messageKey);
        this._emit([optimisticNotification, ...this._cache]);
        this._scheduleRefresh();
    }

    clear(): void {
        this._cache = [];
        this._notifications.next([]);
        this._recentPushTimes.clear();

        if (this._refreshTimer !== null) {
            clearTimeout(this._refreshTimer);
            this._refreshTimer = null;
        }
    }

    private _scheduleRefresh(): void {
        if (this._refreshTimer !== null) {
            return;
        }

        this._refreshTimer = setTimeout(() => {
            this._refreshTimer = null;
            this.getAll().pipe(take(1)).subscribe();
        }, 800);
    }

    private _updateLocalNotification(
        id: string,
        notification: Notification
    ): Observable<Notification> {
        return this.notifications$.pipe(
            take(1),
            map(() => {
                const updatedNotification: Notification = {
                    ...notification,
                    id,
                    active: notification.active ?? true,
                };
                const index = this._cache.findIndex((item) => item.id === id);

                if (index > -1) {
                    const nextNotifications = [...this._cache];
                    nextNotifications[index] = updatedNotification;
                    this._emit(nextNotifications);
                } else {
                    this._emit([updatedNotification, ...this._cache]);
                }

                return updatedNotification;
            })
        );
    }

    private _emit(notifications: Notification[]): Notification[] {
        const dedupedById = new Map<string, Notification>();

        for (const notification of notifications) {
            if (!notification?.id) {
                continue;
            }
            if (notification.active === false) {
                continue;
            }
            dedupedById.set(String(notification.id), {
                ...notification,
                id: String(notification.id),
            });
        }

        const normalized = [...dedupedById.values()]
            .sort(
                (first, second) =>
                    new Date(second.time).getTime() -
                    new Date(first.time).getTime()
            );

        this._cache = normalized;
        this._notifications.next(normalized);
        return normalized;
    }

    private _mapResponse(response: NotificationHistoryResponse): Notification[] {
        const notifications = Array.isArray(response?.notifications)
            ? response.notifications.filter((item) => item?.active !== false)
            : [];

        return notifications.map((item) => this._mapNotificationHistory(item));
    }

    private _mapNotificationHistory(item: NotificationHistoryItem): Notification {
        const orderId = item?.orderId ?? item?.metadata?.orderId;

        return {
            id: String(item?.id ?? this._buildClientNotificationId()),
            title: item?.title ?? 'Notification',
            description: item?.message ?? '',
            time: item?.createdAt ?? new Date().toISOString(),
            read: Boolean(item?.read),
            active: item?.active !== false,
            icon: 'heroicons_outline:bell',
            link: orderId ? '/orders/active' : undefined,
            useRouter: Boolean(orderId),
        };
    }

    private _mapPushPayload(payload: any, messageKey: string): Notification {
        const orderId =
            payload?.data?.orderId ??
            payload?.data?.orderNo ??
            payload?.data?.orderNumber ??
            payload?.data?.orderReference ??
            payload?.data?.orderReferenceId;

        const title =
            payload?.notification?.title ??
            payload?.data?.title ??
            'New Notification';

        const description =
            payload?.notification?.body ?? payload?.data?.body ?? '';

        return {
            id: `push:${messageKey}`,
            title,
            description,
            time: new Date().toISOString(),
            read: false,
            active: true,
            icon: 'heroicons_outline:bell',
            link: orderId ? '/orders/active' : undefined,
            useRouter: Boolean(orderId),
        };
    }

    private _removeLocal(id: string): void {
        const updated = this._cache.filter((item) => item.id !== id);
        this._emit(updated);
    }

    private _isServerNotificationId(id: string): boolean {
        return /^\d+$/.test(id);
    }

    private _buildMessageKey(payload: any): string {
        const orderIdentifier =
            payload?.data?.orderId ??
            payload?.data?.orderNo ??
            payload?.data?.orderNumber ??
            payload?.data?.orderReference ??
            payload?.data?.orderReferenceId;

        if (orderIdentifier) {
            return `order:${String(orderIdentifier).trim()}`;
        }

        const title =
            payload?.notification?.title ?? payload?.data?.title ?? 'notification';
        const body = payload?.notification?.body ?? payload?.data?.body ?? '';

        return `${String(title).trim()}|${String(body).trim()}`;
    }

    private _shouldProcessPush(messageKey: string): boolean {
        const now = Date.now();
        const lastSeenAt = this._recentPushTimes.get(messageKey);

        if (lastSeenAt && now - lastSeenAt < this._pushDedupWindowMs) {
            return false;
        }

        this._recentPushTimes.set(messageKey, now);

        if (this._recentPushTimes.size > 100) {
            for (const [key, seenAt] of this._recentPushTimes.entries()) {
                if (now - seenAt > 15000) {
                    this._recentPushTimes.delete(key);
                }
            }
        }

        return true;
    }

    private _buildClientNotificationId(): string {
        return `local:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
    }
}
